import React, { useState } from 'react';
import { Box, Typography, useTheme, Button, useMediaQuery } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { CurrentHolding } from '../hooks/useCurrentPortfolio';
import HoldingCard from './HoldingCard';
import PortfolioTableView from './PortfolioTableView';
import ViewModeToggle, { ViewMode } from './ViewModeToggle';
import { getFlatPortfolioColors, portfolioTypography } from '../../../../utils/getFlatPortfolioColors';
import AddHoldingsModal from '../../../Portfolio/AddHoldingsModal';

interface PortfolioHoldingsProps {
  holdings: CurrentHolding[];
  isRefetching?: boolean;
}

const PortfolioHoldings: React.FC<PortfolioHoldingsProps> = ({ holdings, isRefetching = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const colors = getFlatPortfolioColors(theme);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [modalOpen, setModalOpen] = useState(false);
  
  // Handle undefined or empty holdings
  if (!holdings || !Array.isArray(holdings) || holdings.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          px: 4,
          backgroundColor: colors.background.secondary,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 0, // Sharp corners - no rounding
        }}
      >
        <Typography 
          sx={{ 
            ...portfolioTypography.title,
            color: colors.text.secondary,
            mb: 2 
          }}
        >
          No Holdings Found
        </Typography>
        <Typography 
          sx={{ 
            ...portfolioTypography.body,
            color: colors.text.muted,
            mb: 3
          }}
        >
          Upload your trading history to see your portfolio holdings here.
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
          sx={{
            borderRadius: 3,
            px: 4,
          }}
        >
          Add or Manage Holdings
        </Button>

        <AddHoldingsModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </Box>
    );
  }

  // Filter out zero-share holdings and sort by current value (descending)
  const sortedHoldings = [...holdings]
    .filter(holding => holding.shares > 0.000001) // Filter out effectively zero holdings
    .sort((a, b) => b.currentValue - a.currentValue);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography
          sx={{
            ...portfolioTypography.title,
            color: colors.text.primary,
            fontSize: '1.25rem',
          }}
        >
          {isMobile ? `HOLDINGS (${holdings.length})` : `INDIVIDUAL HOLDINGS (${holdings.length})`}
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={isMobile ? undefined : <AddIcon />}
            onClick={() => setModalOpen(true)}
            sx={{
              borderRadius: 2,
              minWidth: isMobile ? 'auto' : undefined,
            }}
          >
            {isMobile ? <AddIcon /> : "Add or Manage Holdings"}
          </Button>
          <ViewModeToggle
            mode={viewMode}
            onModeChange={setViewMode}
            disabled={isRefetching}
          />
        </Box>
      </Box>

      {/* Render based on view mode */}
      {viewMode === 'table' ? (
        <PortfolioTableView holdings={sortedHoldings} isRefetching={isRefetching} />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(3, 1fr)',
            },
            gap: 3,
            opacity: isRefetching ? 0.8 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          {sortedHoldings.map((holding) => (
            <HoldingCard key={holding.ticker} holding={holding} />
          ))}
        </Box>
      )}

      <AddHoldingsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default PortfolioHoldings;